<template>
  <div class="intro-layer">
    <div
      v-if="showLoading"
      :class="['loading-veil', { 'is-leaving': loadingLeaving }]"
      aria-hidden="true"
      @animationend.self="removeLoading"
    >
      <AuthBrandLogo mode="pulse" class="brand-logo--loading" />
    </div>

    <div
      v-if="showIntro"
      :class="['intro-veil', { 'is-fading': introFading }]"
      aria-hidden="true"
      @animationstart.self="introFading = true"
      @animationend.self="finishIntro(false)"
    >
      <AuthBrandLogo mode="reveal" class="brand-logo--intro" />
      <div class="intro-word">MANTRAZ</div>
      <div class="intro-sweep"></div>
    </div>
  </div>
</template>

<script>
import AuthBrandLogo from '@/components/auth/AuthBrandLogo';
import { loadingWait, markIntroPlayed } from '@/util/auth/authIntro';
import { createIntroSound } from '@/util/auth/introSound';

const LOADING_MIN = 300;
const LOADING_LEAVE = 900;
const LOADING_GUARD = 4000;
// animasi terakhir (veil out) kelar di 3020 ms, jaringnya dikasih jarak wajar
const INTRO_GUARD = 3800;

export default {
  name: 'AuthIntroSequence',
  components: { AuthBrandLogo },
  props: {
    busy: {
      type: Boolean,
      default: false,
    },
    intro: {
      type: Boolean,
      default: false,
    },
    sound: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showLoading: true,
      loadingLeaving: false,
      showIntro: false,
      introFading: false,
    };
  },
  created() {
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.loadingStartedAt = performance.now();
    this.introStartedAt = 0;
    this.loadingDone = false;
    this.introPlaying = false;
    this.doneEmitted = false;
    this.skipAttached = false;
    this.minTimer = 0;
    this.leaveTimer = 0;
    this.loadingGuard = 0;
    this.introGuard = 0;
    this.audio = null;
  },
  mounted() {
    document.addEventListener('pointerdown', this.onSkipPointer, true);
    document.addEventListener('keydown', this.onKeyDown);
    this.skipAttached = true;
    // jaring pengaman kalau busy gak pernah turun
    this.loadingGuard = setTimeout(() => this.finishLoading(false), LOADING_GUARD);
    this.scheduleLoadingExit();
  },
  beforeDestroy() {
    clearTimeout(this.minTimer);
    clearTimeout(this.leaveTimer);
    clearTimeout(this.loadingGuard);
    clearTimeout(this.introGuard);
    this.detachSkip();
    document.documentElement.classList.remove('is-intro');
    if (this.audio) this.audio.destroy();
    this.audio = null;
  },
  watch: {
    busy(value) {
      if (!value) this.scheduleLoadingExit();
    },
    sound(value) {
      if (value) this.startAudio(); else this.stopAudio();
    },
  },
  methods: {
    /* ---------- loading ---------- */

    scheduleLoadingExit() {
      if (this.busy || this.loadingDone || this.minTimer) return;
      const wait = loadingWait(LOADING_MIN, performance.now() - this.loadingStartedAt);
      this.minTimer = setTimeout(() => {
        this.minTimer = 0;
        this.finishLoading(false);
      }, wait);
    },

    finishLoading(skipped) {
      if (this.loadingDone) return;
      this.loadingDone = true;
      clearTimeout(this.minTimer);
      clearTimeout(this.loadingGuard);
      this.minTimer = 0;
      if (!skipped) this.playIntro();
      // di-skip ya langsung lepas, silang-pudarnya cuma buat serah-terima ke panggung
      if (skipped || this.reduceMotion) {
        this.removeLoading();
        return;
      }
      this.loadingLeaving = true;
      this.leaveTimer = setTimeout(this.removeLoading, LOADING_LEAVE + 140);
    },

    removeLoading() {
      clearTimeout(this.leaveTimer);
      this.leaveTimer = 0;
      this.loadingLeaving = false;
      this.showLoading = false;
      this.settle();
    },

    /* ---------- intro ---------- */

    playIntro() {
      if (this.reduceMotion || !this.intro) {
        this.finishIntro(true);
        return;
      }
      markIntroPlayed();
      document.documentElement.classList.add('is-intro');
      this.introFading = false;
      this.showIntro = true;
      this.introPlaying = true;
      this.introStartedAt = performance.now();
      this.introGuard = setTimeout(() => this.finishIntro(false), INTRO_GUARD);
      this.startAudio();
    },

    finishIntro(skipped) {
      clearTimeout(this.introGuard);
      this.introGuard = 0;
      // selesai wajar dibiarin berdengung sampai ekornya habis sendiri
      if (skipped) this.stopAudio();
      this.introPlaying = false;
      this.showIntro = false;
      this.introFading = false;
      document.documentElement.classList.remove('is-intro');
      this.settle();
    },

    /* ---------- skip ---------- */

    skipAll() {
      this.finishLoading(true);
      this.finishIntro(true);
    },

    onSkipPointer(event) {
      const node = event.target;
      // saklar tema dan suara gak boleh kehitung sebagai klik skip
      if (node && node.closest && node.closest('.auth-controls')) return;
      this.skipAll();
    },

    onKeyDown(event) {
      if (event.key === 'Escape') this.skipAll();
    },

    detachSkip() {
      if (!this.skipAttached) return;
      this.skipAttached = false;
      document.removeEventListener('pointerdown', this.onSkipPointer, true);
      document.removeEventListener('keydown', this.onKeyDown);
    },

    settle() {
      if (this.showLoading || this.showIntro || this.doneEmitted) return;
      this.doneEmitted = true;
      this.detachSkip();
      this.$emit('done');
    },

    /* ---------- backsound ---------- */

    introElapsed() {
      return Math.max(0, (performance.now() - this.introStartedAt) / 1000);
    },

    startAudio() {
      if (this.reduceMotion || !this.sound || !this.introPlaying) return;
      if (!this.audio) this.audio = createIntroSound();
      // nyala di tengah intro pun masuk dari detik berjalan, bukan mengulang
      this.audio.start(this.introElapsed());
    },

    stopAudio() {
      if (this.audio) this.audio.stop();
    },
  },
};
</script>

<style scoped>
.intro-layer { display: contents; }

.loading-veil {
  position: fixed;
  inset: 0;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  will-change: opacity;
  background: var(--veil-bg);
}

/* ghost-nya larut pelan sementara panggung di bawahnya nimpa warna penuh */
.loading-veil.is-leaving {
  pointer-events: none;
  animation: loading-veil-out 900ms cubic-bezier(0.45, 0, 0.3, 1) both;
}

@keyframes loading-veil-out {
  0%   { opacity: 1; }
  32%  { opacity: 0.82; }
  100% { opacity: 0; }
}

/* denyutnya berhenti di posisi utuh dulu, bukan dipotong di tengah napas */
.loading-veil.is-leaving ::v-deep .loading-wave {
  animation: loading-settle 200ms ease-out both;
}

@keyframes loading-settle {
  from { opacity: 0.45; transform: translate3d(0, 1px, 0) scale(0.994); }
  to   { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
}

.intro-veil {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  overflow: hidden;
  will-change: transform, opacity;
  background: var(--veil-bg);
  animation: intro-veil-out 620ms cubic-bezier(0.4, 0, 0.2, 1) 2400ms both;
}

/* begitu lapisannya mulai memudar, klik langsung diteruskan ke panggung */
.intro-veil.is-fading { pointer-events: none; }

@keyframes intro-veil-out {
  from { opacity: 1; transform: scale(1); }
  to   { opacity: 0; transform: scale(1.06); }
}

.intro-word {
  font-family: var(--font-ui);
  font-size: clamp(30px, 7.4vw, 64px);
  font-weight: var(--w-title);
  line-height: 1;
  white-space: nowrap;
  color: var(--ink);
  /* letter-spacing gak punya padanan transform, jadi terpaksa dianimasikan langsung */
  letter-spacing: 0.14em;
  opacity: 0;
  /* jeda 300 ms sesudah gelombang terakhir biar logonya sempat kebaca */
  animation: intro-word-in 620ms cubic-bezier(0.22, 0.78, 0.26, 1) 1480ms both;
}

@keyframes intro-word-in {
  from { opacity: 0; letter-spacing: 0.52em; }
  to   { opacity: 1; letter-spacing: 0.14em; }
}

.intro-sweep {
  width: min(360px, 58vw);
  height: 2px;
  border-radius: 2px;
  opacity: 0;
  background: linear-gradient(
    to right,
    rgba(105, 61, 161, 0) 0%,
    var(--logo-violet) 34%,
    var(--accent) 64%,
    transparent 100%
  );
  animation: intro-sweep-run 700ms cubic-bezier(0.34, 0, 0.22, 1) 1780ms both;
}

@keyframes intro-sweep-run {
  0%   { opacity: 0; transform: translate3d(-118%, 0, 0) scaleX(0.3); }
  28%  { opacity: 1; }
  50%  { transform: translate3d(0, 0, 0) scaleX(1); }
  72%  { opacity: 1; }
  100% { opacity: 0; transform: translate3d(118%, 0, 0) scaleX(0.3); }
}
</style>
